import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Facebook, Instagram, Copy, Link2, Unlink, Loader2 } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { Button } from "../ui/button";

const MSG = { ok: "Facebook connecté", refuse: "Connexion Facebook refusée", erreur: "Échec de la connexion Facebook", "aucune-page": "Aucune Page Facebook autorisée", expire: "Lien de connexion expiré, recommencez" };

export const MetaConnectCard = ({ onChange }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/social/meta/status").then(({ data }) => setSt(data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => {
    load();
    const q = new URLSearchParams(window.location.search).get("meta");
    if (q) {
      (q === "ok" ? toast.success : toast.error)(MSG[q] || "Retour de Facebook");
      window.history.replaceState(null, "", "/admin?section=social");
    }
  }, [load]);
  const connect = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/social/meta/connect"); window.location.href = data.url; }
    catch (e) { toast.error(apiError(e)); setBusy(false); }
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); await load(); onChange(); } catch (e) { toast.error(apiError(e)); } };
  if (!st) return null;
  return (
    <section data-testid="social-meta-card" className="bg-white border border-[#ECE7DE] rounded-2xl p-4 sm:p-5 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-bold text-[#1A1A1A]"><Facebook className="w-4 h-4 text-[#1877F2]" />Connexion Meta directe (principale)</h3>
        {st.connected
          ? <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={connect} disabled={busy} data-testid="social-meta-reconnect-btn"><Link2 className="w-4 h-4 mr-1" />Reconnecter</Button>
              <Button size="sm" variant="outline" onClick={() => act(() => api.post("/admin/social/meta/disconnect"), "Facebook déconnecté")} data-testid="social-meta-disconnect-btn"><Unlink className="w-4 h-4 mr-1" />Déconnecter</Button>
            </div>
          : <Button size="sm" onClick={connect} disabled={busy || !st.app_configured} data-testid="social-meta-connect-btn" className="bg-[#1877F2] hover:bg-[#1466d6] text-white">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Facebook className="w-4 h-4 mr-1" />}Connecter Facebook / Instagram
            </Button>}
      </div>
      {st.connected ? (
        <div data-testid="social-meta-connected" className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
          <span className="inline-flex items-center gap-1.5"><Facebook className="w-4 h-4 text-[#1877F2]" />{st.page_name}</span>
          <span className="inline-flex items-center gap-1.5"><Instagram className="w-4 h-4 text-[#C13584]" />{st.ig_username ? `@${st.ig_username}` : "Instagram non relié à cette Page"}</span>
          {st.user_name && <span className="text-[#75807A]">par {st.user_name}</span>}
        </div>
      ) : (
        <p className="text-sm text-[#5A635D]">{st.app_configured ? "Non connecté : aucune vidéo ne sera publiée sur Facebook tant que la Page Nold Go n'est pas connectée." : "META_APP_ID / META_APP_SECRET manquants dans les secrets."}</p>
      )}
      {st.pages?.length > 1 && (
        <select data-testid="social-meta-page-select" value={st.selected_page_id || ""} className="h-10 rounded-xl border border-[#E2DFD8] bg-white px-3 text-sm"
          onChange={(e) => act(() => api.put("/admin/social/meta/page", { page_id: e.target.value }), "Page sélectionnée")}>
          {st.pages.map((p) => <option key={p.id} value={p.id}>{p.name}{p.ig_username ? ` · @${p.ig_username}` : ""}</option>)}
        </select>
      )}
      {st.error && <p data-testid="social-meta-error" className="text-sm rounded-xl bg-[#FEF1E7] text-[#9A3412] p-3 break-words">{st.error}</p>}
      <div className="flex items-center gap-2 text-xs text-[#75807A] min-w-0">
        <span className="shrink-0">URI de redirection à autoriser dans Meta :</span>
        <code data-testid="social-meta-redirect-uri" className="truncate bg-[#F7F4EF] rounded px-2 py-1">{st.redirect_uri}</code>
        <button data-testid="social-meta-copy-uri" onClick={() => { navigator.clipboard?.writeText(st.redirect_uri); toast.success("Copié"); }} className="shrink-0 hover:text-[#1A1A1A]"><Copy className="w-3.5 h-3.5" /></button>
      </div>
    </section>
  );
};
